import React, { useState, useEffect } from 'react';
import useAuthStore from '../stores/useAuthStore';
import useCourseStore from '../stores/useCourseStore';
import DashboardLayout from '../components/DashboardLayout';
import axiosClient from '../services/api';
import { useToast } from '../components/ToastContainer';

const InstructorCertificateDashboard = () => {
  const { user } = useAuthStore();
  const toast = useToast();
  const { enrolledCourses: myCourses, fetchMyCourses } = useCourseStore();
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [students, setStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchMyCourses();
  }, []);

  const loadStudents = async (courseId) => {
    setIsLoading(true);
    try {
      const res = await axiosClient.get(`/certificates/courses/${courseId}/students`);
      setStudents(res.data.students);
      setSelectedCourse(myCourses.find(c => c._id === courseId));
    } catch (err) {
      toast.error('Failed to load students');
    } finally {
      setIsLoading(false);
    }
  };

  const handleIssueCertificate = async (studentId) => {
    try {
      await axiosClient.post('/certificates/manual', {
        studentId,
        courseId: selectedCourse._id
      });
      toast.success('Certificate issued successfully!');
      loadStudents(selectedCourse._id);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to issue certificate');
    }
  };

  const handleRevokeCertificate = async (certificateId) => {
    try {
      await axiosClient.put(`/certificates/${certificateId}/revoke`);
      toast.success('Certificate revoked!');
      loadStudents(selectedCourse._id);
    } catch (err) {
      toast.error('Failed to revoke certificate');
    }
  };

  return (
    <DashboardLayout userRole={user.role}>
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-neutral-900 dark:text-white">Certificate Management</h1>
            <p className="text-neutral-600 dark:text-neutral-400 mt-2">
              Issue certificates to students who have completed your courses
            </p>
          </div>

          {/* Course Selector */}
          <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6 mb-8">
            <label className="block text-sm font-medium mb-2 dark:text-neutral-300">Select Course</label>
            <select
              onChange={(e) => loadStudents(e.target.value)}
              className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">-- Choose an approved course --</option>
              {myCourses
                .filter(course => course.status === 'approved')
                .map(course => (
                  <option key={course._id} value={course._id}>
                    {course.title}
                  </option>
                ))}
            </select>
          </div>

          {/* Students Table */}
          {selectedCourse && (
            <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 overflow-hidden">
              <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
                <h2 className="text-xl font-semibold dark:text-neutral-300">
                  Students in "{selectedCourse.title}"
                </h2>
              </div>
              {isLoading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                </div>
              ) : students.length > 0 ? (
                <div className="overflow-x-auto dark:text-neutral-300">
                  <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-700">
                    <thead className="bg-neutral-50 dark:bg-neutral-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Student</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Completion</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Certificate</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-neutral-800 divide-y divide-neutral-200 dark:divide-neutral-700">
                      {students.map(student => (
                        <tr key={student._id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="font-medium">{student.name}</div>
                            <div className="text-sm text-neutral-500">{student.email}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-24 bg-neutral-200 dark:bg-neutral-700 rounded-full h-2 mr-2">
                                <div
                                  className="bg-primary-500 h-2 rounded-full"
                                  style={{ width: `${student.completionPercentage}%` }}
                                ></div>
                              </div>
                              <span className="text-sm font-medium">{student.completionPercentage}%</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {student.certificate ? (
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                student.certificate.status === 'issued'
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                  : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                              }`}>
                                {student.certificate.status}
                              </span>
                            ) : (
                              <span className="text-sm text-neutral-500">None</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {student.isCompleted ? (
                              student.certificate?.status === 'issued' ? (
                                <></>
                              ) : (
                                <button
                                  onClick={() => handleIssueCertificate(student._id)}
                                  className="px-3 py-1 bg-green-500 hover:bg-green-600 text-white text-xs rounded"
                                >
                                  Issue
                                </button>
                              )
                            ) : (
                              <span className="text-sm text-neutral-500">Not eligible</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-neutral-600 dark:text-neutral-400">
                    No students enrolled in this course yet.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default InstructorCertificateDashboard;